import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { ActiveCustomer, CustomerNote } from "../../build/activeEntityType";
import { BuildNode, CustomerNotesNode } from "../../build/types";
import { useInteractionStates } from "../../hooks/useInteractionStates";
import { formatTime } from "../../utils/dynamicDataHelpers";
import DynamicIcon from "../DynamicIcon";
import { getCompoundStyle, getStateStyleForBuilder, getStylesFromInteractiveStates } from "../helpers";
import "./style.scss";

const SingleNote = ({
    customerNote,
    isPreview,
    nNode,
    onDelete
}: {
    customerNote: CustomerNote;
    isPreview?: boolean;
    nNode: CustomerNotesNode;
    onDelete?: (note: CustomerNote) => void;
}) => {
    const { t } = useTranslation();

    return (
        <div className="single-note">
            <div className="title-delete">
                {nNode.dateText ? (
                    <p style={nNode.dateText?.style} className="date-text">
                        {formatTime(customerNote?.createdAt, "MMMM do, yyyy")}{" "}
                    </p>
                ) : null}
                {nNode.timeText && (
                    <span style={nNode.timeText.style} className="time-text">
                        {formatTime(customerNote?.createdAt, "HH:mm")}
                    </span>
                )}
            </div>
            <div className="content" style={nNode.noteStyle}>
                {isPreview ? t("previewData." + customerNote?.message) : customerNote?.message}
                {nNode.deleteNoteIcon && (
                    <span
                        onClick={() => {
                            onDelete && onDelete(customerNote);
                        }}
                        className="delete-note"
                    >
                        {nNode.deleteNoteIcon && (
                            <DynamicIcon
                                name={nNode.deleteNoteIcon.src || "square-xmark"}
                                style={nNode.deleteNoteIcon.style}
                                colors={nNode.deleteNoteIcon.colors}
                            />
                        )}
                    </span>
                )}
            </div>
        </div>
    );
};

const BuilderCustomerNotes = ({
    node,
    customer,
    builderProps,
    children,
    emptyState,
    onDelete,
    openAddNotePopup
}: {
    node: BuildNode;
    customer?: ActiveCustomer | null;
    children: React.ReactNode;
    emptyState?: boolean;
    builderProps?: any;
    onDelete?: (note: CustomerNote) => void;
    openAddNotePopup?: () => void;
}) => {
    const nNode = node as CustomerNotesNode;
    const { id, attr, stateManager } = nNode;

    const { setElement, states } = useInteractionStates<HTMLDivElement>();

    const customerNotesClass = classNames("builder-customer-notes", {
        [`${attr?.className}`]: !!attr?.className
    });

    if (builderProps) {
        const { nodeState, selectCallback, outlineCallback, onDragStart, onDragEnd, previewNotes } = builderProps;

        return (
            <div
                {...attr}
                onClick={e => (selectCallback ? selectCallback(e, id) : {})}
                onMouseMove={e => (outlineCallback ? outlineCallback(e, id) : {})}
                id={id}
                className={customerNotesClass}
                style={getCompoundStyle({
                    node,
                    stateStyle: getStateStyleForBuilder(stateManager, nodeState, "attr_style")
                })}
                onDragStart={e => onDragStart?.(e, id)}
                draggable={"true"}
                onDragEnd={onDragEnd}
            >
                <>
                    {nNode.title?.text || nNode.addNoteButton ? (
                        <div className="title-row">
                            {nNode.title?.text && (
                                <p className="title-text" style={nNode.title.style}>
                                    {nNode.title?.text}
                                </p>
                            )}
                            {nNode.addNoteButton?.text && (
                                <button
                                    className="add-note-button"
                                    style={{
                                        ...nNode.addNoteButton?.style,
                                        pointerEvents: "none"
                                    }}
                                >
                                    {nNode.addNoteButton?.icon?.src && (
                                        <DynamicIcon
                                            name={nNode.addNoteButton?.icon?.src}
                                            style={{
                                                ...nNode.addNoteButton?.icon?.style,
                                                pointerEvents: "none"
                                            }}
                                            colors={nNode.addNoteButton.icon.colors}
                                        />
                                    )}
                                    {nNode.addNoteButton?.text}
                                </button>
                            )}
                        </div>
                    ) : null}
                    {nNode.title?.text || nNode.addNoteButton ? <div className="dividera" style={nNode.dividerStyle} /> : null}
                    <div className="notes-content">
                        {emptyState
                            ? children
                            : customer?.notes?.length
                              ? customer.notes.map((note, index) => <SingleNote key={index} customerNote={note} nNode={nNode} />)
                              : previewNotes.map((note: CustomerNote, index: number) => (
                                    <SingleNote key={index} customerNote={note} nNode={nNode} isPreview />
                                ))}
                    </div>
                </>
            </div>
        );
    } else {
        const { style, ...rest } = attr || {};

        const wrapperStyle = getStylesFromInteractiveStates({
            states,
            defaultStyle: getCompoundStyle({ node }),
            stateManager,
            paths: ["attr_style"]
        });

        return (
            <>
                <div {...rest} className={customerNotesClass} ref={setElement} style={wrapperStyle}>
                    {nNode.title?.text || nNode.addNoteButton ? (
                        <div className="title-row">
                            {nNode.title?.text && (
                                <p className="title-text" style={nNode.title.style}>
                                    {nNode.title?.text}
                                </p>
                            )}
                            {nNode.addNoteButton?.text && (
                                <button
                                    className="add-note-button"
                                    onClick={openAddNotePopup}
                                    style={{
                                        ...nNode.addNoteButton?.style
                                    }}
                                >
                                    {nNode.addNoteButton?.icon?.src && (
                                        <DynamicIcon
                                            name={nNode.addNoteButton?.icon?.src}
                                            style={{
                                                ...nNode.addNoteButton?.icon?.style
                                            }}
                                            colors={nNode.addNoteButton.icon.colors}
                                        />
                                    )}
                                    {nNode.addNoteButton?.text}
                                </button>
                            )}
                        </div>
                    ) : null}
                    {nNode.title?.text || nNode.addNoteButton ? <div className="dividera" style={nNode.dividerStyle} /> : null}
                    <div className="notes-content">
                        {customer && customer.notes?.length
                            ? customer.notes.map((note, index) => (
                                  <SingleNote key={index} customerNote={note} nNode={nNode} onDelete={onDelete} />
                              ))
                            : children}
                    </div>
                </div>
            </>
        );
    }
};

export default BuilderCustomerNotes;
